import React, { useState } from "react";
import {
	Box,
	Typography,
	Grid,
	Paper,
	Card,
	Avatar,
	Divider,
	List,
	ListItem,
	ListItemText,
	LinearProgress,
} from "@mui/material";
import {
	TrendingUp,
	TrendingDown,
	ReceiptLong,
	ShowChart,
} from "@mui/icons-material";
import {
	LineChart,
	Line,
	XAxis,
	YAxis,
	Tooltip,
	ResponsiveContainer,
} from "recharts";

// Sample data for metrics
const performanceMetrics = [
	{
		label: "Total Profit",
		value: "$12,450",
		icon: <TrendingUp />,
		color: "success.main",
	},
	{
		label: "Total Loss",
		value: "$1,230",
		icon: <TrendingDown />,
		color: "error.main",
	},
	{ label: "Trades Executed", value: "1,245", icon: <ReceiptLong /> },
	{ label: "Open Positions", value: "32", icon: <ShowChart /> },
];

// Generate sample recent trades with alternating profit/loss
const generateRecentTrades = () => {
	const trades = [];
	const pairs = [
		"BTC/USD",
		"ETH/USD",
		"LTC/USD",
		"XRP/USD",
		"ADA/USD",
		"BNB/USD",
	];
	for (let i = 0; i < 8; i++) {
		const profit = Math.round(Math.random() * 1000);
		const isProfit = i % 2 === 0;
		trades.push({
			tradeId: `TRD${10000 + i}`,
			pair: pairs[i % pairs.length],
			profitLoss: `${isProfit ? "+" : "-"}$${profit}`,
			status: isProfit ? "Completed" : "Closed",
			isProfit,
		});
	}
	return trades;
};

const recentTrades = generateRecentTrades();

// Sample data for Ask and Bid charts
const generateChartData = () => {
	const data = [];
	for (let i = 0; i < 20; i++) {
		data.push({
			time: `T${i}`,
			ask: Math.random() * 50 + 100,
			bid: Math.random() * 50 + 100,
		});
	}
	return data;
};

const chartData = generateChartData();

const TradingPerformancePage = () => {
	const [trades, setTrades] = useState(recentTrades);

	return (
		<Box sx={{ bgcolor: "#f0f2f5", minHeight: "100vh", py: 4 }}>
			<Typography variant="h3" align="center" gutterBottom>
				Trading Performance
			</Typography>

			{/* Metrics Overview */}
			<Grid container spacing={3} px={2} mb={4} justifyContent="center">
				{performanceMetrics.map((metric, index) => (
					<Grid item key={index} xs={12} md={3}>
						<Card
							sx={{
								display: "flex",
								alignItems: "center",
								p: 2,
								height: "100%",
								width: "15rem",
							}}>
							<Avatar sx={{ bgcolor: "primary.main", mr: 2 }}>
								{metric.icon}
							</Avatar>
							<Box>
								<Typography variant="subtitle2" color="textSecondary">
									{metric.label}
								</Typography>
								<Typography
									variant="h6"
									color={metric.color ? metric.color : "textPrimary"}>
									{metric.value}
								</Typography>
							</Box>
						</Card>
					</Grid>
				))}
			</Grid>

			{/* Ask & Bid Charts */}
			<Grid container spacing={3} px={2} mb={4} justifyContent="center">
				{/* Ask Price Chart */}
				<Grid item xs={12} md={6}>
					<Paper sx={{ p: 2, height: 300 }}>
						<Typography gutterBottom variant="h6" align="center">
							Ask Price Movement
						</Typography>
						<ResponsiveContainer width="100%" height="80%">
							<LineChart data={chartData}>
								<XAxis dataKey="time" hide />
								<YAxis domain={["dataMin - 10", "dataMax + 10"]} />
								<Tooltip />
								<Line
									type="monotone"
									dataKey="ask"
									stroke="#1976d2"
									strokeWidth={2}
								/>
							</LineChart>
						</ResponsiveContainer>
					</Paper>
				</Grid>
				{/* Bid Price Chart */}
				<Grid item xs={12} md={6}>
					<Paper sx={{ p: 2, height: 300 }}>
						<Typography gutterBottom variant="h6" align="center">
							Bid Price Movement
						</Typography>
						<ResponsiveContainer width="100%" height="80%">
							<LineChart data={chartData}>
								<XAxis dataKey="time" hide />
								<YAxis domain={["dataMin - 10", "dataMax + 10"]} />
								<Tooltip />
								<Line
									type="monotone"
									dataKey="bid"
									stroke="#d32f2f"
									strokeWidth={2}
								/>
							</LineChart>
						</ResponsiveContainer>
					</Paper>
				</Grid>
			</Grid>

			{/* Recent Trades */}
			<Box px={2} mb={4}>
				<Typography variant="h5" gutterBottom>
					Recent Trades
				</Typography>
				<Paper>
					<List>
						{trades.map((trade, index) => (
							<React.Fragment key={trade.tradeId}>
								<ListItem>
									<ListItemText
										primary={`${trade.pair} (${trade.tradeId})`}
										secondary={`Profit/Loss: `}
										primaryTypographyProps={{ noWrap: true }}
									/>
									<Typography
										variant="subtitle1"
										color={trade.isProfit ? "success.main" : "error.main"}>
										{trade.profitLoss}
									</Typography>
								</ListItem>
								{index < trades.length - 1 && <Divider />}
							</React.Fragment>
						))}
					</List>
				</Paper>
			</Box>

			{/* Performance Summary / KPIs */}
			<Box px={2}>
				<Typography variant="h5" gutterBottom>
					Performance Summary
				</Typography>
				<Grid container spacing={3}>
					{/* Total Return */}
					<Grid item xs={12} md={4}>
						<Paper sx={{ p: 2, bgcolor: "white" }}>
							<Typography gutterBottom>Total Return</Typography>
							<Typography variant="h4" color="success.main">
								+45%
							</Typography>
							<LinearProgress variant="determinate" value={70} sx={{ mt: 1 }} />
						</Paper>
					</Grid>
					{/* Win Rate */}
					<Grid item xs={12} md={4}>
						<Paper sx={{ p: 2, bgcolor: "white" }}>
							<Typography gutterBottom>Win Rate</Typography>
							<Typography variant="h4" color="success.main">
								68%
							</Typography>
							<LinearProgress variant="determinate" value={68} sx={{ mt: 1 }} />
						</Paper>
					</Grid>
					{/* Max Drawdown */}
					<Grid item xs={12} md={4}>
						<Paper sx={{ p: 2, bgcolor: "white" }}>
							<Typography gutterBottom>Max Drawdown</Typography>
							<Typography variant="h4" color="error.main">
								12%
							</Typography>
							<LinearProgress variant="determinate" value={12} sx={{ mt: 1 }} />
						</Paper>
					</Grid>
				</Grid>
			</Box>
		</Box>
	);
};

export default TradingPerformancePage;
